/**
 * PTE Academic Landing — /resources/pte-academic
 *
 * Section overview + question-type grid + Today's PTE Challenge widget.
 * Reuses global Winhouse header/footer.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pteApi, DISCLAIMER } from '@/lib/pteApi';
import { Mic, BookOpen, Headphones, PenTool, Sparkles, Trophy, ChevronRight, Loader2, PlayCircle, Timer, Award, Type, ArrowRight } from 'lucide-react';
import Seo from '@/components/shared/Seo';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import PTEDailyChallengeWidget from '@/components/pte/PTEDailyChallengeWidget';

const SKILL_ICONS = { speaking: Mic, writing: PenTool, reading: BookOpen, listening: Headphones };
const SKILL_ACCENT = {
  speaking: 'from-emerald-500 to-teal-600',
  writing: 'from-amber-500 to-orange-600',
  reading: 'from-sky-500 to-indigo-600',
  listening: 'from-fuchsia-500 to-pink-600',
};

export default function PTELandingPage() {
  const [config, setConfig] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([pteApi.config(), pteApi.taskTypes()]);
        setConfig(c.config);
        setTasks(t.items || []);
      } catch (_) {}
    })();
  }, []);

  if (!config) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <div className="min-h-[60vh] flex items-center justify-center gap-2 text-slate-500 pt-24">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading PTE Academic…
        </div>
      </div>
    );
  }

  const tasksBySkill = tasks.reduce((acc, t) => {
    (acc[t.skill] = acc[t.skill] || []).push(t); return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="pte-landing">
      <Seo
        title="PTE Academic Preparation · Winhouse Tutors"
        description="Build the skills, strategies and confidence you need for PTE Academic. Original practice tasks across all 22 scored question types."
        canonical="/resources/pte-academic"
      />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16 md:pb-24 text-white">
          <Link to="/resources" className="text-slate-500 hover:text-slate-800 text-xs inline-flex items-center gap-1 mb-4" data-testid="pte-back-to-resources">
            <ArrowRight className="h-3 w-3 rotate-180" /> Back to Resources
          </Link>
          <div className="text-xs uppercase tracking-widest text-emerald-300 mb-3">Resource Centre</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight max-w-3xl text-[#172033]">
            PTE Academic <span className="text-emerald-300">Preparation</span>
          </h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">
            Build the skills, strategies and confidence you need for PTE Academic — with original Winhouse-authored practice across every scored task type.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/resources/pte-academic/practice/pte_read_aloud"
              data-testid="pte-cta-start"
              className="px-6 py-3 rounded-full bg-emerald-400 hover:bg-emerald-300 text-black font-bold text-sm inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Start practising
            </Link>
            <a href="#skills"
              data-testid="pte-cta-explore"
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white text-sm inline-flex items-center gap-2">
              Explore PTE skills <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Overview cards */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { title: "Question-Type Practice", body: "Practise any one of 22 scored PTE task types with immediate feedback where possible.", to: "#task-types" },
            { title: "Today's PTE Challenge", body: "A rotating daily task. Logged-in students earn +5 XP for a correct attempt.", to: "#daily" },
            { title: "Skill Practice", body: "Focus on Speaking, Writing, Reading or Listening.", to: "#skills" },
          ].map((c, i) => (
            <a key={i} href={c.to} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-slate-400 transition"
              data-testid={`pte-overview-${i}`}>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Section</div>
              <div className="text-lg font-bold text-slate-900 mb-1">{c.title}</div>
              <div className="text-sm text-slate-600">{c.body}</div>
            </a>
          ))}
        </div>
      </section>

      {/* Daily Challenge */}
      <section id="daily" className="max-w-6xl mx-auto px-4 md:px-6 pb-10">
        <PTEDailyChallengeWidget />
      </section>

      {/* Pathways strip — student's main entry points */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-10" data-testid="pte-pathways">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Choose a pathway</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { to: '/resources/pte-academic/learn', label: 'Learn & Revise', desc: 'Strategies for every task type', tid: 'pathway-learn' },
            { to: '/resources/pte-academic#simulation', label: 'Section Practice', desc: 'Timed simulation by section', tid: 'pathway-section' },
            { to: '/resources/pte-academic#mock', label: 'Full PTE Mock', desc: 'Complete 3-section mock', tid: 'pathway-mock' },
            { to: '/pte/vocab', label: 'Vocabulary Builder', desc: '156 curated entries + practice', tid: 'pathway-vocab' },
          ].map((p) => (
            <Link key={p.tid} to={p.to} data-testid={p.tid}
              className="rounded-2xl bg-white border border-slate-200 hover:border-slate-900 p-4 transition group">
              <div className="text-sm font-bold text-slate-900">{p.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{p.desc}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-slate-900 text-xs font-semibold group-hover:gap-1.5 transition-all">
                Open <ChevronRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Simulation Mode CTAs */}
      <section id="simulation" className="max-w-6xl mx-auto px-4 md:px-6 pb-10">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white p-6 md:p-8 border border-emerald-800/40">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 inline-flex items-center gap-2">
                <Timer className="h-3.5 w-3.5" /> Full simulation
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Practice under real exam conditions</h2>
              <p className="text-slate-300 text-sm md:text-base">
                A dedicated Winhouse assessment shell with server-authoritative timers, autosave and a Results Centre that shows your <em>Winhouse Estimated PTE Practice Score</em>.
              </p>
            </div>
            <div className="text-xs text-slate-400 max-w-xs">
              For the closest PTE simulation experience, we recommend a desktop or laptop with microphone access.
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-6">
            {[
              { key: 'speaking_writing', label: 'Speaking & Writing', mins: 60, icon: Mic, tint: 'from-emerald-500 to-teal-600' },
              { key: 'reading', label: 'Reading', mins: 30, icon: BookOpen, tint: 'from-sky-500 to-indigo-600' },
              { key: 'listening', label: 'Listening', mins: 45, icon: Headphones, tint: 'from-fuchsia-500 to-pink-600' },
            ].map(({ key, label, mins, icon: Icon, tint }) => (
              <Link key={key} to={`/pte/simulation/${key}`}
                data-testid={`pte-sim-cta-${key}`}
                className="group rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 p-5 transition backdrop-blur">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tint} flex items-center justify-center mb-3`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="text-lg font-bold">{label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{mins} minute simulation</div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-300 text-sm font-semibold group-hover:gap-2 transition-all">
                  <PlayCircle className="h-4 w-4" /> Start simulation
                </div>
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-4">
            Practice attempts are graded on objective items automatically; speaking &amp; writing responses are queued for tutor review.
          </div>
        </div>
      </section>

      {/* Full Mock + Vocabulary Builder */}
      <section id="mock" className="max-w-6xl mx-auto px-4 md:px-6 pb-10 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-3xl bg-gradient-to-br from-indigo-900 via-slate-900 to-emerald-900 text-white p-6 md:p-8 border border-indigo-800/40">
          <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 inline-flex items-center gap-2">
            <Award className="h-3.5 w-3.5" /> Full PTE Academic Mock
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Complete Winhouse practice simulation</h2>
          <p className="text-slate-300 text-sm md:text-base mb-4 max-w-xl">
            Experience a complete timed Winhouse PTE Academic practice simulation across Speaking &amp; Writing, Reading and Listening — under simulation conditions. Approx. 2h 15m total.
          </p>
          <div className="text-[11px] text-slate-400 mb-4">
            For the closest simulation experience, use a desktop or laptop with microphone access.
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { key: 'mock_v1', label: 'Mock 1', tint: 'from-emerald-500 to-teal-600' },
              { key: 'mock_v2', label: 'Mock 2', tint: 'from-sky-500 to-indigo-600' },
              { key: 'mock_v3', label: 'Mock 3', tint: 'from-fuchsia-500 to-pink-600' },
            ].map(({ key, label, tint }) => (
              <Link key={key} to={`/pte/mock/${key}`} data-testid={`pte-mock-cta-${key}`}
                className="group rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 p-4 transition backdrop-blur">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${tint} flex items-center justify-center mb-2`}>
                  <Timer className="h-4 w-4 text-white" />
                </div>
                <div className="text-base font-bold">Winhouse {label}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-emerald-300 text-xs font-semibold group-hover:gap-1.5 transition-all">
                  <PlayCircle className="h-3.5 w-3.5" /> Start full mock
                </div>
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-4">
            Winhouse Tutors provides independent PTE Academic preparation and is not affiliated with or endorsed by Pearson.
          </div>
        </div>
        <Link to="/pte/vocab" data-testid="pte-vocab-cta"
          className="group rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 text-white p-6 border border-amber-400/40 flex flex-col justify-between hover:shadow-2xl transition">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-100 mb-2 inline-flex items-center gap-2">
              <Type className="h-3.5 w-3.5" /> Vocabulary Builder
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-2">Boost academic vocab</h3>
            <p className="text-amber-50 text-sm">Curated word bank across 10 categories plus 5 practice modes and XP.</p>
          </div>
          <div className="mt-6 inline-flex items-center gap-1.5 text-white font-semibold group-hover:gap-2 transition-all">
            Open vocabulary <ChevronRight className="h-4 w-4" />
          </div>
        </Link>
      </section>

      {/* Skill overview */}
      <section id="skills" className="max-w-6xl mx-auto px-4 md:px-6 pb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Skills</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {['speaking', 'writing', 'reading', 'listening'].map((s) => {
            const Icon = SKILL_ICONS[s];
            const count = (tasksBySkill[s] || []).length;
            return (
              <div key={s} className="rounded-2xl overflow-hidden bg-white border border-slate-200"
                data-testid={`pte-skill-card-${s}`}>
                <div className={`h-24 bg-gradient-to-br ${SKILL_ACCENT[s]} flex items-end p-4 text-white`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" />
                    <div className="text-xl font-bold capitalize">{s}</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-sm text-slate-600">{count} task types available.</div>
                  <div className="mt-3 space-y-1">
                    {(tasksBySkill[s] || []).slice(0, 3).map((t) => (
                      <div key={t.key} className="text-xs text-slate-700">• {t.label}</div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Task-type grid */}
      <section id="task-types" className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        {config.sections.map((sec) => (
          <div key={sec.code} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">{sec.label}</div>
              <div className="text-xs text-slate-400">{sec.task_types.length} task types</div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sec.task_types.map((t) => (
                <Link key={t.key} to={`/resources/pte-academic/practice/${t.key}`}
                  data-testid={`pte-task-${t.key}`}
                  className="group rounded-xl bg-white border border-slate-200 p-4 hover:border-slate-900 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
                        {t.needs_mic && 'Speaking · '}
                        {t.needs_audio_stimulus && !t.needs_mic && 'Listening · '}
                        {t.label.match(/writing/i) && !t.needs_mic ? 'Writing · ' : ''}
                        {!t.scored && 'Non-scored · '}
                        {t.skill}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 group-hover:underline">
                        {t.label}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Disclaimer */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16 text-xs text-slate-500 border-t border-slate-200 pt-6">
        {DISCLAIMER}
      </section>
      <Footer />
    </div>
  );
}
